import React, { useContext } from "react";
import { useTranslate, sendTealiumEvent } from "../../helpers";
import { Button } from "@cmsgov/ds-medicare-gov";
import { Routes } from "../../app";
import { useHistory, useLocation } from "react-router";
import {
  ScrollTable,
  CheckmarkIcon,
  HelpDrawerWrapper,
} from "../../components";
import { AppStore } from "../../app/store";
import { DrugCostInfo, PharmacyType, PlanDetailsSection } from "../../@types";

interface PharmacyInfo {
  npi: string;
  name: string;
  in_network: boolean;
  mail_order: boolean;
  preferred: boolean;
}

const Pharmacies: React.FC<{
  drugCostInfo: DrugCostInfo;
}> = ({ drugCostInfo: { costs: pharmacyCosts } }) => {
  const {
    state: { pharmacies: selectedPharmacies, pharmacyType },
  } = useContext(AppStore.AppContext);
  const t = useTranslate();

  const pharmacies: PharmacyInfo[] = pharmacyCosts.map(thisPharmacy => {
    const { npi, in_network, mail_order, preferred } = thisPharmacy;
    const name =
      selectedPharmacies.find(pharm => pharm.npi === npi)?.name ||
      t("plan_details.pharmacies.mail_order_pharmacy");

    return { npi, name, in_network, mail_order, preferred };
  });

  const showMailOrderNotCovered =
    !pharmacies.find(p => p.mail_order === true) &&
    [PharmacyType.MAIL_AND_RETAIL, PharmacyType.MAIL].includes(pharmacyType);

  const history = useHistory();
  const location = useLocation();

  const sectionId = PlanDetailsSection.PHARMACIES;

  const handleClick = (): void => {
    sendTealiumEvent("Plan Details", "Change Pharmacies");

    const pharmacyLink = Routes.pharmacy;
    const nextRoute = location;
    nextRoute.hash = `#${sectionId}`;
    history.push({ pathname: pharmacyLink, state: { nextRoute } });
  };

  const PharmacyRow: React.FC<{ pharmacy: PharmacyInfo }> = ({ pharmacy }) => {
    return (
      <tr
        data-testid={`retail-${
          pharmacy.in_network ? "in-network" : "out-of-network"
        }`}
      >
        <td>
          <strong>{pharmacy.name}</strong>
        </td>
        <td className="PlanDetailsPharmacies__NetworkStatus">
          <CheckmarkIcon checked={pharmacy.in_network} />
        </td>
        <td>
          {pharmacy.in_network
            ? pharmacy.preferred
              ? t("plan_details.pharmacies.preferred_in_network")
              : t("plan_details.pharmacies.standard_in_network")
            : t("plan_details.pharmacies.out_of_network_pharmacy")}
        </td>
      </tr>
    );
  };

  const MailOrderRow: React.FC<{ isCovered: boolean }> = ({ isCovered }) => {
    if (isCovered) {
      return (
        <tr data-testid="mail-order-info">
          <td colSpan={2}>
            <strong>{t("plan_details.pharmacies.mail_order_pharmacy")}</strong>
          </td>
          <td>{t("plan_details.pharmacies.costs_vary_for_mail_order")}</td>
        </tr>
      );
    }
    return (
      <tr data-testid="mail-order-not-covered">
        <td>
          <strong>{t("plan_details.pharmacies.mail_order_pharmacy")}</strong>
        </td>
        <td className="PlanDetailsPharmacies__NetworkStatus">
          <CheckmarkIcon checked={false} />
        </td>
        <td>{t("plan_terms.does_not_cover_mail_order")}</td>
      </tr>
    );
  };

  return (
    <div className="PlanDetailsPharmacies">
      <div className="ds-u-display--flex ds-u-flex-wrap--wrap ds-u-justify-content--between mct-u-full-width">
        <h2 className="ds-h2" id={sectionId} tabIndex={-1}>
          {t("plan_card.section_heading.pharmacies")}
        </h2>
        <Button
          className="ds-c-button--primary-alt-outline"
          onClick={handleClick}
        >
          {t("plan_details.pharmacies.change_pharmacies")}
        </Button>
      </div>
      <div>
        <p>{t("plan_card.pharmacies.description")}</p>
        <HelpDrawerWrapper
          toggleText={t("plan_card.pharmacies.more_about_pharmacy_cost_levels")}
          drawerTitle={t(
            "plan_card.pharmacies.more_about_pharmacy_cost_levels"
          )}
        >
          <p>{t("plan_card.pharmacies.help_drawer.intro_text")}</p>
          <ul>
            <li>
              {t("plan_card.pharmacies.help_drawer.preferred_in_network")}
            </li>
            <li>{t("plan_card.pharmacies.help_drawer.standard_in_network")}</li>
            <li>{t("plan_card.pharmacies.help_drawer.out_of_network")}</li>
            <li>{t("plan_card.pharmacies.help_drawer.mail_order")}</li>
          </ul>
        </HelpDrawerWrapper>
        <ScrollTable>
          <tbody>
            {pharmacies.map(pharmacy =>
              pharmacy.mail_order ? (
                [PharmacyType.MAIL, PharmacyType.MAIL_AND_RETAIL].includes(
                  pharmacyType
                ) && <MailOrderRow key="mail_order_row" isCovered={true} />
              ) : (
                <PharmacyRow key={pharmacy.npi} pharmacy={pharmacy} />
              )
            )}
            {showMailOrderNotCovered && <MailOrderRow isCovered={false} />}
          </tbody>
        </ScrollTable>
      </div>
      <div className="ds-u-margin-y--6">
        <hr />
      </div>
    </div>
  );
};

export default Pharmacies;
